import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import ArchivedToggle from '../ui/ArchivedToggle';
import FilterPopover from '../ui/FilterPopover';
import {
  GIFT_FOREVER_EVENT, GIFT_NEW_EVENT, GIFT_RESTORE_EVENT, GIFT_SHARE_EVENT, setGiftRegistryBar, useGiftRegistryBar,
} from '../../hooks/useGiftRegistryBar';
import { useRole } from '../../hooks/useRole';
import { GIFT_KIND_OPTIONS, GIFT_LIST_PATH, GIFT_PRIORITY_OPTIONS } from '../../utils/giftRegistry';

const send = (name: string) => window.dispatchEvent(new CustomEvent(name));

/** Gift Registry list actions in the Header's bar: Filters (Type, Priority), the Archived switch, list/cards, Share, New item. */
export const GiftRegistryListBar: React.FC = () => {
  const bar = useGiftRegistryBar();
  const { canEdit } = useRole();
  return (
    <>
      <FilterPopover
        selects={[
          { key: 'kind', label: 'Type', options: [{ value: '', label: 'Gifts and requests' }, ...GIFT_KIND_OPTIONS], value: bar.kind, defaultValue: '', onChange: kind => setGiftRegistryBar({ kind }) },
          { key: 'priority', label: 'Priority', options: [{ value: '', label: 'Any priority' }, ...GIFT_PRIORITY_OPTIONS], value: bar.priority, defaultValue: '', onChange: priority => setGiftRegistryBar({ priority }) },
        ]}
      />
      <div className="header-filter-group">
        <ArchivedToggle checked={bar.archived} onChange={archived => setGiftRegistryBar({ archived })} />
      </div>
      <div className="gr-layout-switch" role="group" aria-label="Layout">
        <button type="button" className="header-action-btn" aria-pressed={bar.layout === 'list'} onClick={() => setGiftRegistryBar({ layout: 'list' })}>List</button>
        <button type="button" className="header-action-btn" aria-pressed={bar.layout === 'cards'} onClick={() => setGiftRegistryBar({ layout: 'cards' })}>Cards</button>
      </div>
      {canEdit && (
        <button type="button" className="header-action-btn header-action-btn--labelled" title="Share the registry" onClick={() => send(GIFT_SHARE_EVENT)}>
          <Icon name="link" size={16} />
          <span className="header-action-btn__label">Share</span>
        </button>
      )}
      {canEdit && !bar.archived && (
        <button type="button" className="header-action-btn header-action-btn--labelled primary" title="New item" onClick={() => send(GIFT_NEW_EVENT)}>
          <Icon name="plus" size={16} />
          <span className="header-action-btn__label">New item</span>
        </button>
      )}
    </>
  );
};

/** The open item's actions: Save, Cancel, and Archive (or Restore and Delete forever once archived). */
export const GiftItemBar: React.FC = () => {
  const { editor } = useGiftRegistryBar();
  const navigate = useNavigate();
  if (!editor.canEdit) return null;
  return (
    <>
      {!editor.archived && (
        <button type="button" className="header-action-btn header-action-btn--labelled primary" disabled={editor.saving} title="Save" onClick={() => send('form-save')}>
          <Icon name="check" size={16} />
          <span className="header-action-btn__label">{editor.saving ? 'Saving...' : 'Save'}</span>
        </button>
      )}
      <button type="button" className="header-action-btn" onClick={() => navigate(GIFT_LIST_PATH)}>Cancel</button>
      {!editor.isNew && !editor.archived && (
        <button type="button" className="header-action-btn header-action-btn--labelled header-btn-danger" title="Archive this item" onClick={() => send('form-delete')}>
          <Icon name="trash" size={16} />
          <span className="header-action-btn__label">Archive</span>
        </button>
      )}
      {!editor.isNew && editor.archived && (
        <>
          <button type="button" className="header-action-btn header-action-btn--labelled" title="Restore" onClick={() => send(GIFT_RESTORE_EVENT)}>
            <Icon name="refresh" size={16} />
            <span className="header-action-btn__label">Restore</span>
          </button>
          <button type="button" className="header-action-btn header-action-btn--labelled header-btn-danger" title="Delete forever" onClick={() => send(GIFT_FOREVER_EVENT)}>
            <Icon name="trash" size={16} />
            <span className="header-action-btn__label">Delete forever</span>
          </button>
        </>
      )}
    </>
  );
};
